import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Filter, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

type Row = {
  id: string;
  created_at: string;
  table_name: string;
  record_id: string | null;
  action: string;
  actor_user_id: string | null;
  actor_role: string | null;
  actor_email: string | null;
  old_data: any;
  new_data: any;
};

const TABLES = [
  "shelters",
  "shelter_updates",
  "shelter_owners",
  "shelter_claim_requests",
  "shelter_claim_tokens",
  "shelter_succession_proposals",
  "shelter_team_invites",
];

const ACTIONS = ["INSERT", "UPDATE", "DELETE"];

export const AuditLogTab = () => {
  const [tableFilter, setTableFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [actorEmail, setActorEmail] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["audit-log", tableFilter, actionFilter],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_list_audit_log", {
        _table: tableFilter === "all" ? null : tableFilter,
        _action: actionFilter === "all" ? null : actionFilter,
        _actor: null,
        _limit: 200,
        _offset: 0,
      });
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = actorEmail.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (r) =>
        (r.actor_email ?? "").toLowerCase().includes(q) ||
        (r.actor_user_id ?? "").toLowerCase().includes(q),
    );
  }, [data, actorEmail]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="w-4 h-4" /> Filtry
          </CardTitle>
          <CardDescription>Filtruj log zmian po tabeli, akcji i osobie wykonującej.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Tabela</Label>
            <Select value={tableFilter} onValueChange={setTableFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie</SelectItem>
                {TABLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Akcja</Label>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie</SelectItem>
                {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Osoba (email lub UUID)</Label>
            <Input
              value={actorEmail}
              onChange={(e) => setActorEmail(e.target.value)}
              placeholder="np. admin@..."
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="font-display font-semibold text-lg">Log zdarzeń</h2>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{filtered.length} pozycji</Badge>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : "Odśwież"}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Brak wpisów dla wybranych filtrów.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <Collapsible key={r.id}>
              <Card>
                <CardContent className="py-3">
                  <CollapsibleTrigger asChild>
                    <button className="w-full text-left flex items-center justify-between gap-3 group">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <Badge variant="outline" className="font-mono text-[10px]">{r.table_name}</Badge>
                        <Badge
                          variant={r.action === "DELETE" ? "destructive" : r.action === "INSERT" ? "default" : "secondary"}
                          className="text-[10px]"
                        >
                          {r.action}
                        </Badge>
                        <span className="text-xs text-muted-foreground truncate">
                          {r.actor_email ?? r.actor_user_id ?? "-"} <span className="opacity-60">({r.actor_role ?? "-"})</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                          {format(new Date(r.created_at), "d MMM yyyy, HH:mm:ss", { locale: pl })}
                        </span>
                        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                      </div>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3 space-y-2">
                    <div className="text-xs text-muted-foreground">
                      record_id: <span className="font-mono">{r.record_id ?? "-"}</span>
                    </div>
                    {r.old_data && (
                      <details>
                        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">old_data</summary>
                        <pre className="mt-1 max-h-72 overflow-auto rounded bg-muted p-2 text-[10px]">{JSON.stringify(r.old_data, null, 2)}</pre>
                      </details>
                    )}
                    {r.new_data && (
                      <details open>
                        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">new_data</summary>
                        <pre className="mt-1 max-h-72 overflow-auto rounded bg-muted p-2 text-[10px]">{JSON.stringify(r.new_data, null, 2)}</pre>
                      </details>
                    )}
                  </CollapsibleContent>
                </CardContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditLogTab;
